import { Button, Modal, Radio } from '@/ui';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import './AsignarEncargadoModal.css';

// Avatar de iniciales — no importa nada externo.
function Initials({ name }) {
  const parts = (name || '').split(' ').filter(Boolean);
  const ini =
    parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : (parts[0]?.[0] ?? '?').toUpperCase();
  return (
    <span className="encargado-avatar" aria-hidden="true" title={name}>
      {ini}
    </span>
  );
}

Initials.propTypes = {
  name: PropTypes.string,
};

/**
 * Modal para asignar o cambiar el encargado de una causa.
 *
 * Props:
 * - open: bool
 * - onClose: fn()
 * - members: [{ id, nombre }]  ← miembros del org (del query de la página Configuración)
 * - currentEncargadoId: string | null  ← ID del encargado actual
 * - onSave: fn(assigneeId: string | null)  ← null = "Sin asignar"
 * - saving: bool
 */
export default function AsignarEncargadoModal({
  open,
  onClose,
  members,
  currentEncargadoId,
  onSave,
  saving,
}) {
  const [selected, setSelected] = useState(currentEncargadoId ?? null);

  // Reset when the modal reopens with a different causa
  useEffect(() => {
    if (open) setSelected(currentEncargadoId ?? null);
  }, [open, currentEncargadoId]);

  const options = [{ id: null, nombre: 'Sin asignar' }, ...(members || [])];

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header title="Asignar encargado" onClose={onClose} />
      <Modal.Body>
        <div
          className="encargado-list"
          role="radiogroup"
          aria-label="Seleccionar encargado"
        >
          {options.map((opt) => (
            <div
              key={opt.id ?? '__none__'}
              className={`encargado-row${selected === opt.id ? ' encargado-row--selected' : ''}`}
              onClick={() => !saving && setSelected(opt.id)}
              role="none"
            >
              <Radio
                checked={selected === opt.id}
                onChange={() => !saving && setSelected(opt.id)}
                aria-label={opt.nombre}
                disabled={saving}
              />
              {opt.id !== null && <Initials name={opt.nombre} />}
              <span className="encargado-name">{opt.nombre}</span>
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Foot>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
          disabled={saving}
        >
          Cancelar
        </Button>
        <Button
          variant="seal"
          type="button"
          onClick={() => onSave(selected)}
          disabled={saving}
        >
          {saving ? 'Guardando…' : 'Guardar'}
        </Button>
      </Modal.Foot>
    </Modal>
  );
}

AsignarEncargadoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  members: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, nombre: PropTypes.string }),
  ),
  currentEncargadoId: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};
